import { Box, Card, Table, TableBody, TableContainer } from '@mui/material';
import { TableHeader } from '../../components/formlib/TableHeader';
import ActivityTableRow, { StatusType, UpdateTime } from './ActivityTableRow';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import {
  Activity,
  ActivitySubmitRequest,
  archiveAnAppointment,
  submitWeeklyActivitySheet,
  updateAppointment,
} from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { STORAGE_USER_ID_KEY } from '../../services/Constant';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { EmployeeType, ResponseType } from '../../utils/type';
import { getHeaderValues } from './activityUtils';
import ModalPopup from '../../components/formlib/ModalPopup';
import { debounce } from 'lodash';
import {
  ApiError,
  ApiMessage,
  isCustomError,
} from '../../services/ApiResponseHandler';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import moment from 'moment';
import { GetServiceAndSettingsCode } from '../../services/progressNotes/GetServiceAndSettingsCode';
import { CodeListType } from '../ProgressNote/ProgressNote';
import { LookUpContext } from '../../context/LookUpContextProvider';
import {
  GroupBilling,
  UpdateGroupBillig,
} from '../../services/progressNotes/ProgressNotesApi';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import { useNavigate } from 'react-router-dom';
import { ACTIVITY_APPROVAL_OVERVIEW_SHEET_ROUTE_NAME } from '../../routes/Routing';
import { ActivitySheetHeaderType } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface HourlyNonExemptEmployeeHeader {
  day: string;
  billableHoursDec: string;
  approvedNonBillingHrs: string;
  otherAssocWorkHrsDec: string;
  noShowTravelWaitDoc: string;
  otherNonBillableHrsDec: string;
  hrsOut?: string;
  lunchHrs: string;
  totalHrs: string;
  status: string;
  actions: string;
}

export interface SalariedNonExemptEmployeeHeader {
  day: string;
  billableHoursDec: string;
  approvedNonBillingHrs: string;
  otherNonBillableHrsDec: string;
  otherAssocWorkHrsDec: string;
  hrsOut?: string;
  lunchHrs: string;
  totalHrs: string;
  status: string;
  actions: string;
}

export interface SalariedEmployeeHeader {
  day: string;
  billableHoursDec: string;
  approvedNonBillingHrs: string;
  otherNonBillableHrsDec: string;
  hrsOut?: string;
  totalHrs: string;
  status: string;
  actions: string;
}

interface TableViewProps {
  selectedDate?: Date;
  filterStatus: string | null;
  activities: Activity[];
  employeeType: EmployeeType | null;
  status: StatusType | null;
  reload: boolean;
  setReload: React.Dispatch<React.SetStateAction<boolean>>;
  isEmployee: boolean;
  updateActivities?: (
    activitySheetId: string | null,
    status: StatusType,
  ) => void;
  submitApprovedActivities?: () => void;
  updateToOriginalStatus?: () => void;
  screenName: string;
  handleNoteUpdate: (activitySheetId: string, note: string) => void;
}

const ActivityListTableView: FC<TableViewProps> = ({
  selectedDate,
  employeeType,
  activities,
  setReload,
  reload,
  isEmployee,
  updateActivities,
  submitApprovedActivities,
  handleNoteUpdate,
  updateToOriginalStatus,
  screenName,
  status,
  filterStatus,
}) => {
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [toastrId, setToastrId] = useState<string>();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [checkBoxEnabled, setCheckBoxEnabled] = useState<boolean>(
    isEmployee ? false : activities.length !== 0,
  );
  const [headerCells, setHeaderCells] = useState<ActivitySheetHeaderType>([]);
  const { updateServiceAndSettingCodes } = useContext(LookUpContext);
  const [confirmationPopup, setConfirmationPopup] = useState<boolean>(false);
  const [totalHoursAddedPopup, setTotalHoursAddedPopup] =
    useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (filterStatus === null) {
      setCheckBoxEnabled(isEmployee ? false : activities.length !== 0);
    } else {
      setCheckBoxEnabled(false);
    }
  }, [filterStatus, isEmployee, activities.length]);

  useEffect(() => {
    if (employeeType === null) {
      return;
    }
    const cells = getHeaderValues(employeeType);
    if (screenName === 'EmployeeActivity') {
      cells.push(
        {
          id: 'status',
          labelId: 'Dashboard.Status',
          defaultLabelId: 'Status',
          numeric: false,
          requiredSorting: false,
        },
        {
          id: 'actions',
          labelId: 'actions',
          defaultLabelId: 'Actions',
          numeric: false,
          requiredSorting: false,
        },
      );
    }
    setHeaderCells(cells);
  }, [employeeType]);

  useEffect(() => {
    GetServiceAndSettingsCode()
      .then((coderesponse: CodeListType) => {
        const serviceCodes = coderesponse.serviceCode.map((code) => ({
          id: code.id,
          label: code.code,
        }));
        const settingsCodes = coderesponse.settingCode.map((code) => ({
          id: code.id,
          label: code.code,
        }));
        updateServiceAndSettingCodes(serviceCodes, settingsCodes);
      })
      .catch(() => {});
  }, []);

  const hasIncompleteProgressNotes = (activity: Activity): boolean => {
    if (activity.archived || activity.archivalStatus === 'Submitted') {
      return false;
    }

    return activity.groupBilling.some((group) =>
      group.appointments.some((appointment) => {
        if (
          appointment.archived ||
          appointment.onHold ||
          appointment.attendeeRole === 'INTERPRETER' ||
          appointment.attendeeRole === 'GROUP_ASSISTANT'
        ) {
          return false;
        }
        return (
          appointment.progressNotesStatus === 'Pending' ||
          appointment.progressNotesStatus === 'In Progress'
        );
      }),
    );
  };

  const checkProgressNotesCompletion = (activityArray: Activity[]): boolean => {
    return !activityArray.some(hasIncompleteProgressNotes);
  };

  const employeeSubmit = useCallback(
    debounce((activityArray: Activity[]) => {
      submitActivitySheet(activityArray);
    }, 300),
    [],
  );

  const hasActivitiesToSubmit = () => {
    const pendingActivities = activities.filter((activity) => {
      return (
        activity.status === null ||
        activity.status === 'Not Submitted' ||
        activity.status === 'Rejected'
      );
    });
    return pendingActivities.length > 0;
  };

  const filterGroupBilling = (activity: Activity) => {
    return activity.groupBilling.filter((group) => {
      return group.appointments.some(
        (appt) => appt.status === null || appt.status === 'Rejected',
      );
    });
  };

  const checkAllTotalHoursAdded = () => {
    if (status === null || status === 'Not Submitted') {
      for (const activity of activities) {
        if (activity.totalHoursDec === null || activity.totalHoursDec === 0.0) {
          return false; // Break and return immediately
        }
      }
      return true;
    } else {
      return true; // All activities have valid hours
    }
  };

  const convertGroupBillingDurations = (activity: Activity) => {
    for (const group of activity.groupBilling) {
      group.groupBillingDurationDec =
        group.groupBillingDurationDec === null
          ? null
          : group.groupBillingDurationDec;
    }
  };

  const collectProgressNotesIds = (activity: Activity): string[] => {
    const subIds: string[] = [];
    if (
      activity.status === null ||
      activity.status === 'Not Submitted' ||
      activity.status === 'Rejected'
    ) {
      for (const group of activity.groupBilling) {
        for (const appt of group.appointments) {
          subIds.push(appt.progressNotesId);
        }
      }
    }
    return subIds;
  };

  const shouldIncludeActivity = (activity: Activity): boolean => {
    return (
      (activity.archivalStatus === null ||
        activity.archivalStatus === 'Pending') &&
      (activity.status === 'Not Submitted' || activity.status === 'Rejected')
    );
  };

  const calculateWeekDates = () => {
    const currentDate = dayjs(selectedDate);
    const newStartDate = currentDate.date(
      currentDate.date() - currentDate.day(),
    );
    const newEndDate = currentDate.date(
      currentDate.date() + (6 - currentDate.day()),
    );

    const startDateInUTC =
      moment(newStartDate.startOf('day').format(), 'YYYY-MM-DDTHH:mm:ss')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';
    const endDateDateInUTC =
      moment(newEndDate.endOf('day').format(), 'YYYY-MM-DDTHH:mm:ss')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    return { startDateInUTC, endDateDateInUTC };
  };

  const submitActivitySheet = (activityArray: Activity[]) => {
    const userId = localStorage.getItem(STORAGE_USER_ID_KEY);
    if (userId === null) {
      return;
    }

    const submitRequest: ActivitySubmitRequest = {
      employeeId: userId,
      activities: [],
      startDate: '',
      endDate: '',
    };

    for (const activity of activityArray) {
      convertGroupBillingDurations(activity);

      if (shouldIncludeActivity(activity)) {
        const subIds = collectProgressNotesIds(activity);
        const filteredGroupBilling = filterGroupBilling(activity);

        submitRequest.activities.push({
          progressNotesIds: subIds,
          groupBilling: activity.archived ? [] : filteredGroupBilling,
          activitySheetId: activity.activitySheetId,
          status: activity.status,
        });
      }
    }

    if (submitRequest.activities.length === 0) {
      return;
    }

    const { startDateInUTC, endDateDateInUTC } = calculateWeekDates();
    submitRequest.startDate = startDateInUTC;
    submitRequest.endDate = endDateDateInUTC;

    toggleLoader(true);
    submitWeeklyActivitySheet(submitRequest)
      .then(() => {
        setSuccessOrError('success');
        setToastrId('activitySubmitSuccessMessage');
        setToastrDefaultMessage('Activity sheet submitted successfully');
        toggleLoader(false);
        setReload(true);
        setCheckBoxEnabled(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          const response = error as ApiMessage;
          if (response.code === 13001) {
            setToastrId('progressNoteNotSignedMessage');
            setToastrDefaultMessage(response.message);
          } else if (response.code === 13002) {
            setToastrId('timeOffNotApprovedMessage');
            setToastrDefaultMessage(response.message);
          } else if (response.code === 1057) {
            setToastrId('activitySheetGroupBillingErrorMessage');
            setToastrDefaultMessage(response.message);
          } else {
            setToastrId('activitySubmitFailureMessage');
            setToastrDefaultMessage('Failed to submit Activity sheet');
          }
        }
      });
  };

  const archiveItem = (
    activitySheetId: string,
    progressNotesId: string,
    archive: boolean,
  ) => {
    toggleLoader(true);
    archiveAnAppointment(activitySheetId, progressNotesId, archive)
      .then(() => {
        setSuccessOrError('success');
        if (archive) {
          setToastrId('archiveSuccessMessage');
          setToastrDefaultMessage('Archived successfully');
        } else {
          setToastrId('unArchiveSuccessMessage');
          setToastrDefaultMessage('Unarchived successfully');
        }
        toggleLoader(false);
        setReload(true);
        setCheckBoxEnabled(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('archiveFailureMessage');
          setToastrDefaultMessage('Archive Failed');
        }
      });
  };

  const updateGroupBillingFromActivitySheet = (data: GroupBilling) => {
    toggleLoader(true);
    UpdateGroupBillig(data)
      .then(() => {
        setSuccessOrError('success');
        setToastrId('GroupBilling.UpdatedSuccess');
        setToastrDefaultMessage('Group Billing Updated Successfully');
        toggleLoader(false);
        setReload(true);
      })
      .catch(() => {
        setSuccessOrError('error');
        setToastrId('GroupBilling.UpdatedError');
        setToastrDefaultMessage('Failed to Update Group Billing');
        toggleLoader(false);
      });
  };

  const updateTimeOrNote = (
    activitySheetId: string,
    data: UpdateTime,
    updateType: 'NOTE' | 'TIME',
  ) => {
    toggleLoader(true);
    updateAppointment(
      dayjs(selectedDate).format('MM/DD/YYYY'),
      {
        activitySheetId: activitySheetId,
        description: data.note,
        approvedNonBillableHoursDec: data.approvedNonBillableHoursDec,
        otherAssocWorkHrsDec: data.otherAssocWorkHrsDec,
        otherNonBillableHrsDec: data.otherNonBillableHrsDec,
        totalHoursDec: data.totalHoursDec,
        lunchTimeIncluded: data.lunchTimeIncluded,
        noShowTravelDocDec: data.noShowTravelDocDec,
        activityDate: data.activityDate,
        employeeType: employeeType,
      },
      screenName === 'ApprovalActivitySheet',
      localStorage.getItem(STORAGE_USER_ID_KEY) as string,
    )
      .then(() => {
        setSuccessOrError('success');
        if (updateType === 'NOTE') {
          if (data.note !== undefined) {
            handleNoteUpdate(activitySheetId, data.note);
          }
          setToastrId('noteUpdateSuccessMessage');
          setToastrDefaultMessage('Note updated successfully');
        } else {
          setToastrId('dataUpdatedTextMessage');
          setToastrDefaultMessage('Data Updated Successfully');
          setReload(true);
        }
        toggleLoader(false);
        setCheckBoxEnabled(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else if (updateType === 'NOTE') {
          setToastrId('noteUpdateFailureMessage');
          setToastrDefaultMessage('Failed to update note');
        } else {
          setToastrId('dataUpdateFailMessage');
          setToastrDefaultMessage('Data Failed to Update');
        }
      });
  };

  return (
    <Box
      component={'section'}
      sx={{
        marginTop: '12px',
      }}
    >
      {toastrId && !reload && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
          successOrError={successOrError}
        />
      )}
      {confirmationPopup && (
        <ModalPopup
          open={confirmationPopup}
          maxWidth={'xs'}
          description="activitySheetConfirmationDesc"
          onCancel={() => {
            setCheckBoxEnabled(false);
            setConfirmationPopup(false);
          }}
          onClose={() => {
            setCheckBoxEnabled(false);
            setConfirmationPopup(false);
          }}
          onOk={() => {
            setConfirmationPopup(false);
            employeeSubmit(activities);
          }}
          labelId1="cancelText"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Yesbtn"
          positiveActionLabel="Yes"
        />
      )}

      {totalHoursAddedPopup && (
        <ModalPopup
          open={totalHoursAddedPopup}
          maxWidth={'sm'}
          description={'totalHoursNotAddedTextMessage'}
          defaultDescription="You have not updated the Total Hours for one or more days. This may result in the activity sheet being rejected. Are you sure you want to proceed?"
          onCancel={() => {
            setCheckBoxEnabled(false);
            setTotalHoursAddedPopup(false);
          }}
          onClose={() => {
            setCheckBoxEnabled(false);
            setTotalHoursAddedPopup(false);
          }}
          onOk={() => {
            const alreadySubmitted = hasActivitiesToSubmit();
            if (alreadySubmitted && activities.length > 0) {
              setTotalHoursAddedPopup(false);
              setConfirmationPopup(true);
            }
          }}
          labelId1="Clientpage.Nobtn"
          negativeActionLabel="No"
          labelId2="Clientpage.Yesbtn"
          positiveActionLabel="Yes"
        />
      )}
      <Card sx={{ margin: '8px 0px' }}>
        <TableContainer>
          <Table>
            <TableHeader
              className="activityHeader listDataTableHead"
              labelSxProp={{ whiteSpace: 'normal' }}
              headerNames={headerCells}
              checkBoxRequired={false}
            />
            <TableBody className="tableRowcss">
              {activities?.map((item) => {
                return (
                  <ActivityTableRow
                    key={item.activitySheetId}
                    props={item}
                    status={status}
                    headerCount={headerCells.length}
                    employeeType={employeeType}
                    isEmployee={isEmployee}
                    onStatusUpdate={(activitySheetId, s) => {
                      if (updateActivities) {
                        updateActivities(activitySheetId, s);
                      }
                    }}
                    updateTimeOrNote={(
                      activitySheetId: string,
                      data: UpdateTime,
                      updateType: 'NOTE' | 'TIME',
                    ) => {
                      updateTimeOrNote(activitySheetId, data, updateType);
                    }}
                    archiveItem={(
                      activitySheetId: string,
                      progressNotesId: string,
                      archive: boolean,
                    ) => {
                      archiveItem(activitySheetId, progressNotesId, archive);
                    }}
                    updateGroupBilling={(data) => {
                      updateGroupBillingFromActivitySheet(data);
                    }}
                    screenName={screenName}
                    selectedDate={selectedDate}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {screenName === 'EmployeeActivity' && selectedDate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CheckBoxComponent
            ischecked={checkBoxEnabled}
            CheckHandleChange={(e) => {
              setCheckBoxEnabled(e.target.checked);
            }}
            disabled={!hasActivitiesToSubmit() && filterStatus === null}
            labelid={'activityAgreeDesc'}
            defaultlabelid="By clicking on this checkbox, you are attesting to its accuracy."
          />
        </Box>
      )}
      {screenName === 'EmployeeActivity' && (
        <Box className="mb-xlg" display={'flex'} justifyContent={'end'}>
          <ButtonComponent
            className="btn-primary btn-submit ml-md"
            variantType="contained"
            type="submit"
            labelId={'submitText'}
            disabled={isEmployee ? !checkBoxEnabled : activities.length === 0}
            onClick={() => {
              const progressNotesCompleted =
                checkProgressNotesCompletion(activities);

              if (!progressNotesCompleted) {
                setSuccessOrError('error');
                setToastrId('pendingProgressNoteValidationMessage');
                setToastrDefaultMessage(
                  'One or more Progress Notes are not completed. Please complete all Progress Notes to submit your Activity Sheet.',
                );
                return;
              }

              const totalHoursAdded = checkAllTotalHoursAdded();
              if (!totalHoursAdded) {
                setTotalHoursAddedPopup(true);
                return;
              }
              const activitiesToSubmit = hasActivitiesToSubmit();
              if (activitiesToSubmit && activities.length > 0) {
                setConfirmationPopup(true);
              }
            }}
          />
        </Box>
      )}
      {screenName === 'ApprovalActivitySheet' &&
        status !== null &&
        status !== 'Rejected' &&
        status !== 'Not Submitted' && (
          <Box
            className="mt-md mb-xlg"
            display={'flex'}
            justifyContent={'end'}
            paddingTop={'16px'}
          >
            <ButtonComponent
              className="btn-primary btn-cancel"
              variantType="contained"
              type="submit"
              labelId={'cancelText'}
              defaultLabelId="Cancel"
              onClick={() => {
                if (updateToOriginalStatus) {
                  let updatedActivities = false;
                  for (const element of activities) {
                    if (element.status !== element.originalStatus) {
                      updatedActivities = true;
                      break;
                    }
                  }
                  if (updatedActivities) {
                    updateToOriginalStatus();
                  } else {
                    navigate(ACTIVITY_APPROVAL_OVERVIEW_SHEET_ROUTE_NAME, {
                      replace: true,
                      state: {
                        date: selectedDate,
                      },
                    });
                  }
                }
              }}
            />

            <ButtonComponent
              className="btn-primary btn-submit ml-md"
              variantType="contained"
              type="submit"
              labelId={'saveText'}
              defaultLabelId="Save"
              onClick={() => {
                if (submitApprovedActivities) {
                  submitApprovedActivities();
                }
              }}
            />
          </Box>
        )}
    </Box>
  );
};

export default ActivityListTableView;
